import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Users,
  ShieldAlert,
  ShieldCheck,
  Ban,
  Check,
  X,
  Loader2,
  Mail,
  Pencil,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/context/AuthContext";
import { DashboardSidebar } from "@/components/DashboardSidebar";
import { FullScreenLoader } from "@/components/FullScreenLoader";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

/* /admin/users — admin-only user management.

   Lists every registered SalesPit user (with search + status filter),
   lets the admin open any row to view full details, edit name / username
   username, or experience level, and block or unblock the account. All API calls go through
   /api/admin/users*, which the backend gates on ADMIN_EMAILS.
*/

const STATUS_FILTERS = [
  { value: "", label: "All" },
  { value: "active", label: "Active" },
  { value: "blocked", label: "Blocked" },
];

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function Avatar({ user, size = 36 }) {
  const initial = (user?.name || user?.email || "?").charAt(0).toUpperCase();
  if (user?.picture) {
    return (
      <img
        src={user.picture}
        alt=""
        className="shrink-0 rounded-full object-cover"
        style={{ height: size, width: size }}
      />
    );
  }
  return (
    <span
      className="shrink-0 inline-flex items-center justify-center rounded-full bg-white/5 text-sm font-bold text-zinc-200"
      style={{ height: size, width: size }}
    >
      {initial}
    </span>
  );
}

function StatusPill({ blocked, online }) {
  if (blocked) {
    return (
      <span
        data-testid="status-pill-blocked"
        className="inline-flex items-center gap-1.5 rounded-full border border-[#E52525]/35 bg-[#E52525]/10 px-2.5 py-1 text-[11px] font-semibold text-[#FF6565]"
      >
        <Ban size={11} /> Blocked
      </span>
    );
  }
  return (
    <span
      data-testid="status-pill-active"
      className="inline-flex items-center gap-1.5 rounded-full border border-[#22C55E]/30 bg-[#22C55E]/10 px-2.5 py-1 text-[11px] font-semibold text-[#22C55E]"
    >
      <span
        className={`h-1.5 w-1.5 rounded-full ${online ? "bg-[#22C55E]" : "bg-[#22C55E]/40"}`}
      />
      Active{online ? " · Online" : ""}
    </span>
  );
}

export default function AdminUsers() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("loading"); // loading|denied|ok
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selected, setSelected] = useState(null); // user_id of row being viewed/edited

  const fetchList = useCallback(async () => {
    const params = new URLSearchParams();
    if (q.trim()) params.set("q", q.trim());
    if (statusFilter) params.set("status", statusFilter);
    try {
      const r = await api.get(`/admin/users?${params.toString()}`);
      setUsers(r.data?.items || []);
      setTotal(r.data?.total || 0);
      setStatus("ok");
    } catch (err) {
      setStatus(err?.response?.status === 403 ? "denied" : "denied");
    }
  }, [q, statusFilter]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  if (status === "loading") return <FullScreenLoader />;

  return (
    <div className="flex min-h-screen bg-[#0A0A0A] font-body text-white">
      <DashboardSidebar active="admin-users" />
      <main
        className="flex-1 px-6 py-7 md:px-12 md:py-10"
        data-testid="admin-users-page"
      >
        <div className="mx-auto max-w-6xl">
          <div>
            <p className="mb-1 text-xs font-semibold uppercase tracking-[0.18em] text-[#E52525]">
              Admin
            </p>
            <h1 className="font-heading text-3xl font-black tracking-tight sm:text-4xl">
              Users
            </h1>
            <p className="mt-2 text-sm text-zinc-400">
              Every registered SalesPit user. Click a row to view, edit, or
              block them.
            </p>
          </div>

          {/* Secondary tab link to the Feedback admin page */}
          <div className="mt-6 inline-flex rounded-full border border-white/10 bg-[#141414] p-1 text-sm">
            <button
              type="button"
              data-testid="admin-tab-users"
              className="rounded-full bg-white/10 px-4 py-1.5 font-semibold text-white"
            >
              Users
            </button>
            <button
              type="button"
              data-testid="admin-tab-feedback"
              onClick={() => navigate("/admin/feedback")}
              className="rounded-full px-4 py-1.5 font-medium text-zinc-400 hover:text-white"
            >
              Feedback
            </button>
          </div>

          {status === "denied" && (
            <div
              data-testid="admin-denied"
              className="mt-10 flex items-start gap-3 rounded-2xl border border-[#E52525]/30 bg-[#E52525]/5 p-5"
            >
              <ShieldAlert size={20} className="mt-0.5 shrink-0 text-[#E52525]" />
              <div>
                <p className="font-semibold text-white">Admin access required</p>
                <p className="mt-1 text-sm text-zinc-400">
                  Your account can't view this page.
                </p>
                <button
                  onClick={() => navigate("/homepage")}
                  className="mt-3 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-sm font-semibold text-white hover:bg-white/10"
                >
                  Back home
                </button>
              </div>
            </div>
          )}

          {status === "ok" && (
            <>
              {/* Search + filter row */}
              <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
                <div className="relative flex-1">
                  <Search
                    size={14}
                    className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500"
                  />
                  <input
                    data-testid="admin-user-search"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search by email, name, or @username"
                    className="w-full rounded-full border border-white/10 bg-[#141414] py-2.5 pl-10 pr-4 text-sm text-white outline-none focus:border-white/25"
                  />
                </div>
                <div className="inline-flex rounded-full border border-white/10 bg-[#141414] p-1 text-xs">
                  {STATUS_FILTERS.map((f) => (
                    <button
                      key={f.value || "all"}
                      type="button"
                      data-testid={`admin-filter-${f.value || "all"}`}
                      onClick={() => setStatusFilter(f.value)}
                      className={`rounded-full px-3 py-1.5 font-semibold transition-colors ${
                        statusFilter === f.value
                          ? "bg-white/10 text-white"
                          : "text-zinc-400 hover:text-white"
                      }`}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Users table */}
              <div className="mt-6 overflow-hidden rounded-2xl border border-white/10 bg-[#141414]">
                {users.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-14 text-center">
                    <Users size={28} className="text-zinc-600" />
                    <p className="mt-3 text-sm text-zinc-400">
                      No users match these filters.
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table
                      data-testid="admin-users-table"
                      className="w-full min-w-[720px] text-sm"
                    >
                      <thead className="bg-white/5 text-xs uppercase tracking-[0.14em] text-zinc-500">
                        <tr>
                          <th className="px-5 py-3.5 text-left font-semibold">User</th>
                          <th className="px-5 py-3.5 text-left font-semibold">Email</th>
                          <th className="px-5 py-3.5 text-left font-semibold">Status</th>
                          <th className="px-5 py-3.5 text-left font-semibold">Joined</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((u) => (
                          <tr
                            key={u.user_id}
                            data-testid={`admin-user-row-${u.user_id}`}
                            onClick={() => setSelected(u.user_id)}
                            className="cursor-pointer border-t border-white/5 align-middle hover:bg-white/[0.03]"
                          >
                            <td className="px-5 py-4">
                              <div className="flex items-center gap-3">
                                <Avatar user={u} />
                                <div>
                                  <p className="font-medium text-white">
                                    {u.name || "—"}
                                  </p>
                                  {u.username && (
                                    <p className="text-xs text-zinc-500">
                                      @{u.username}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-5 py-4 text-zinc-300">{u.email}</td>
                            <td className="px-5 py-4">
                              <StatusPill blocked={u.is_blocked} online={u.is_online} />
                            </td>
                            <td className="whitespace-nowrap px-5 py-4 text-xs text-zinc-500">
                              {formatDate(u.created_at)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <p className="mt-3 text-xs text-zinc-600">
                Showing {users.length} of {total} user{total === 1 ? "" : "s"}.
              </p>
            </>
          )}
        </div>
      </main>

      <UserDetailModal
        userId={selected}
        onClose={() => setSelected(null)}
        onChanged={fetchList}
      />
    </div>
  );
}

/* ----------- Details / Edit / Block modal ----------- */

function UserDetailModal({ userId, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ name: "", username: "" });
  const [saving, setSaving] = useState(false);
  const [blockReason, setBlockReason] = useState("");
  const [acting, setActing] = useState(false);

  useEffect(() => {
    if (!userId) {
      setData(null);
      setEditing(false);
      setBlockReason("");
      return;
    }
    let alive = true;
    setLoading(true);
    (async () => {
      try {
        const r = await api.get(`/admin/users/${encodeURIComponent(userId)}`);
        if (!alive) return;
        setData(r.data);
        setDraft({
          name: r.data?.name || "",
          username: r.data?.username || "",
        });
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Couldn't load user.");
        onClose();
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const save = async () => {
    if (!data) return;
    setSaving(true);
    try {
      const r = await api.patch(
        `/admin/users/${encodeURIComponent(data.user_id)}`,
        {
          name: draft.name,
          username: draft.username,
        }
      );
      setData(r.data);
      setEditing(false);
      onChanged?.();
      toast.success("User updated.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't update user.");
    } finally {
      setSaving(false);
    }
  };

  const block = async () => {
    if (!data) return;
    setActing(true);
    try {
      const r = await api.post(
        `/admin/users/${encodeURIComponent(data.user_id)}/block`,
        { reason: blockReason }
      );
      setData(r.data);
      setBlockReason("");
      onChanged?.();
      toast.success(`${r.data.name || r.data.email} is now blocked.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't block user.");
    } finally {
      setActing(false);
    }
  };

  const unblock = async () => {
    if (!data) return;
    setActing(true);
    try {
      const r = await api.post(
        `/admin/users/${encodeURIComponent(data.user_id)}/unblock`
      );
      setData(r.data);
      onChanged?.();
      toast.success(`${r.data.name || r.data.email} is active again.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't unblock user.");
    } finally {
      setActing(false);
    }
  };

  return (
    <Dialog open={!!userId} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        data-testid="admin-user-modal"
        className="max-w-[640px] w-[calc(100vw-2rem)] border-white/10 bg-[#141414] p-0 text-white sm:rounded-2xl"
      >
        {loading || !data ? (
          <div className="flex items-center justify-center py-14">
            <DialogTitle className="sr-only">User details</DialogTitle>
            <DialogDescription className="sr-only">
              Loading user details
            </DialogDescription>
            <Loader2 size={22} className="animate-spin text-zinc-500" />
          </div>
        ) : (
          <div className="p-7 sm:p-8">
            <div className="flex items-start gap-4">
              <Avatar user={data} size={56} />
              <div className="min-w-0 flex-1">
                <DialogTitle asChild>
                  <h2 className="truncate font-heading text-2xl font-black tracking-tight">
                    {data.name || "—"}
                  </h2>
                </DialogTitle>
                <DialogDescription asChild>
                  <p className="mt-0.5 flex items-center gap-2 text-sm text-zinc-400">
                    <Mail size={13} /> {data.email}
                  </p>
                </DialogDescription>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <StatusPill blocked={data.is_blocked} online={data.is_online} />
                  {data.username && (
                    <span className="text-xs text-zinc-500">@{data.username}</span>
                  )}
                </div>
              </div>
            </div>

            {/* Blocked note */}
            {data.is_blocked && (
              <div
                data-testid="admin-user-block-note"
                className="mt-5 rounded-xl border border-[#E52525]/30 bg-[#E52525]/5 p-4 text-sm text-zinc-300"
              >
                <p className="font-semibold text-white">Account blocked</p>
                <p className="mt-1 text-xs text-zinc-400">
                  Since {formatDate(data.blocked_at)}
                </p>
                {data.blocked_reason && (
                  <p className="mt-2 italic text-zinc-300">
                    "{data.blocked_reason}"
                  </p>
                )}
              </div>
            )}

            {/* Details / Edit */}
            <div className="mt-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-xs font-semibold uppercase tracking-[0.14em] text-zinc-500">
                  Account details
                </h3>
                {!editing ? (
                  <button
                    data-testid="admin-user-edit-btn"
                    onClick={() => setEditing(true)}
                    className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-transparent px-3 py-1 text-xs font-semibold text-zinc-300 hover:border-white/25 hover:text-white"
                  >
                    <Pencil size={11} /> Edit
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <button
                      data-testid="admin-user-cancel-btn"
                      onClick={() => {
                        setEditing(false);
                        setDraft({
                          name: data.name || "",
                          username: data.username || "",
                        });
                      }}
                      className="rounded-full border border-white/10 px-3 py-1 text-xs font-semibold text-zinc-300 hover:bg-white/5"
                    >
                      Cancel
                    </button>
                    <button
                      data-testid="admin-user-save-btn"
                      onClick={save}
                      disabled={saving}
                      className="inline-flex items-center gap-1.5 rounded-full bg-[#E52525] px-3 py-1 text-xs font-semibold text-white hover:bg-[#FF3333] disabled:opacity-60"
                    >
                      {saving && <Loader2 size={11} className="animate-spin" />}
                      Save
                    </button>
                  </div>
                )}
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Name">
                  {editing ? (
                    <input
                      data-testid="admin-user-field-name"
                      value={draft.name}
                      onChange={(e) =>
                        setDraft((d) => ({ ...d, name: e.target.value }))
                      }
                      className="w-full rounded-lg border border-white/10 bg-[#0F0F0F] px-3 py-2 text-sm text-white outline-none focus:border-white/25"
                    />
                  ) : (
                    <ReadValue>{data.name || "—"}</ReadValue>
                  )}
                </Field>
                <Field label="Username">
                  {editing ? (
                    <input
                      data-testid="admin-user-field-username"
                      value={draft.username}
                      onChange={(e) =>
                        setDraft((d) => ({
                          ...d,
                          username: e.target.value.toLowerCase(),
                        }))
                      }
                      className="w-full rounded-lg border border-white/10 bg-[#0F0F0F] px-3 py-2 text-sm text-white outline-none focus:border-white/25"
                    />
                  ) : (
                    <ReadValue>
                      {data.username ? `@${data.username}` : "—"}
                    </ReadValue>
                  )}
                </Field>
                <Field label="Experience">
                  <ReadValue>
                    {data.experience_level || "—"}
                  </ReadValue>
                </Field>
                <Field label="Joined">
                  <ReadValue>{formatDate(data.created_at)}</ReadValue>
                </Field>
                <Field label="Sessions logged">
                  <ReadValue>{data.session_count ?? "—"}</ReadValue>
                </Field>
              </div>
            </div>

            {/* Block / Unblock */}
            <div className="mt-7 border-t border-white/5 pt-6">
              {data.is_blocked ? (
                <button
                  data-testid="admin-user-unblock-btn"
                  onClick={unblock}
                  disabled={acting}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#22C55E] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#19a04a] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {acting ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
                  Unblock user
                </button>
              ) : (
                <>
                  <label className="block text-xs font-semibold uppercase tracking-[0.14em] text-zinc-500">
                    Block reason <span className="text-zinc-600">· optional</span>
                  </label>
                  <input
                    data-testid="admin-user-block-reason"
                    value={blockReason}
                    onChange={(e) => setBlockReason(e.target.value)}
                    placeholder="Repeated harassment reports"
                    className="mt-2 w-full rounded-lg border border-white/10 bg-[#0F0F0F] px-3 py-2 text-sm text-white outline-none focus:border-white/25"
                  />
                  <button
                    data-testid="admin-user-block-btn"
                    onClick={block}
                    disabled={acting}
                    className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#E52525] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#FF3333] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {acting ? <Loader2 size={14} className="animate-spin" /> : <Ban size={14} />}
                    Block user
                  </button>
                </>
              )}
            </div>

            <button
              type="button"
              aria-label="Close"
              onClick={onClose}
              className="absolute right-4 top-4 rounded-full p-1 text-zinc-500 hover:bg-white/5 hover:text-white"
            >
              <X size={16} />
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const Field = ({ label, children }) => (
  <div>
    <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-zinc-500">
      {label}
    </p>
    {children}
  </div>
);

const ReadValue = ({ children }) => (
  <p className="text-sm text-zinc-200">{children}</p>
);
